import * as RadixTabs from '@radix-ui/react-tabs'
import { forwardRef, type RefObject, useEffect, useRef, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'
import * as styles from './styles'
import {
  type TabsListProps,
  type TabsPanelProps,
  type TabsRootProps,
  type TabsTabProps,
} from './types'

export {
  type TabsListProps,
  type TabsPanelProps,
  type TabsRootProps,
  type TabsTabProps,
} from './types'

///////////////////////////////////////////////////////////////////////////////////////
// `TabsLike` is a collection of reusable pieces for building accessible tabs.
//
// You get consistent behavior and styling without doing a bunch of overrides on another component. This makes the
// system simpler to use and easier to maintain.
///////////////////////////////////////////////////////////////////////////////////////

// @note Doesn't consider whether matching elements are actually visible, only whether they're present
const TABBABLE_SELECTOR = [
  'a[href]',
  'audio[controls]',
  'button:not([disabled])',
  'details > summary',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'video[controls]',
  '[contenteditable]:not([contenteditable=false])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * If the panel doesn't contain any tabbable elements, the panel itself acts as a tab stop; otherwise the user wouldn't
 * be able to move focus to the panel.
 */
function useIsPanelTabStop(panelRef: RefObject<HTMLElement>): boolean {
  // Radix's default implementation
  const [isTabStop, setIsTabStop] = useState(true)

  useEffect(() => {
    const panel = panelRef.current
    if (panel === null) return

    const check = (): void =>
      setIsTabStop(panel.querySelector(TABBABLE_SELECTOR) === null)
    check()

    // Tabbable elements may come and go without the panel re-rendering (for example, if it contains a form whose submit
    // button becomes disabled), so this has to monitor the DOM
    const observer = new MutationObserver(check)
    observer.observe(panel, {
      attributeFilter: [
        'contenteditable',
        'controls',
        'disabled',
        'href',
        'tabindex',
      ],
      childList: true,
      subtree: true,
    })
    return () => observer.disconnect()
  }, [panelRef])

  return isTabStop
}

/**
 * @note Radix implements all the markup and behavior
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const TabsRoot = forwardRef<HTMLDivElement, TabsRootProps>(
  function TabsRoot(props, ref) {
    const { activation = 'automatic', ...remainingProps } = props

    return (
      <RadixTabs.Root
        activationMode={activation}
        ref={ref}
        {...remainingProps}
      />
    )
  }
)
TabsRoot.displayName = 'Lapidary: TabsRoot ← TabsLike'

/**
 * @note Radix implements all the markup and behavior
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  function TabsList(props, ref) {
    return <RadixTabs.List ref={ref} {...props} />
  }
)
TabsList.displayName = 'Lapidary: TabsList ← TabsLike'

/**
 * Whether the rendered tab has something a screen reader can announce. Tabs take arbitrary children, so a tab that only
 * contains an (aria-hidden) icon would otherwise silently have no name.
 */
function hasAccessibleName(tab: HTMLElement): boolean {
  if (tab.getAttribute('aria-label') || tab.getAttribute('aria-labelledby'))
    return true

  // `textContent` includes text that's hidden from assistive technology
  const announced = tab.cloneNode(true) as HTMLElement
  for (const hidden of announced.querySelectorAll('[aria-hidden=true]'))
    hidden.remove()
  return (announced.textContent ?? '').trim() !== ''
}

/**
 * @note Radix implements all the markup and behavior
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const TabsTab = forwardRef<HTMLButtonElement, TabsTabProps>(
  function TabsTab(props, ref) {
    // The type system can't see what `children` will render, so manually enforce the accessible name at runtime (but
    // only in development).
    const localRef = useRef<HTMLButtonElement>(null)
    useEffect(() => {
      if (process.env.NODE_ENV === 'production' || localRef.current === null)
        return

      if (!hasAccessibleName(localRef.current)) {
        throw new TypeError(
          'Tab: must have child text, aria-label, or aria-labelledby'
        )
      }
    })

    return <RadixTabs.Trigger ref={mergeRefs([localRef, ref])} {...props} />
  }
)
TabsTab.displayName = 'Lapidary: TabsTab ← TabsLike'

/**
 * @warning Components outside `@opallabs/lapidary` MUST NOT use this.
 */
export const TabsPanel = forwardRef<HTMLDivElement, TabsPanelProps>(
  function TabsPanel(props, ref) {
    const { keepMounted = false, tabIndex, ...remainingProps } = props

    const localRef = useRef<HTMLDivElement>(null)
    const isTabStop = useIsPanelTabStop(localRef)

    return (
      <RadixTabs.Content
        css={styles.panel}
        ref={mergeRefs([localRef, ref])}
        {...remainingProps}
        // Radix defaults to `tabIndex={0}`, so this needs an explicit `undefined` to remove it from the tab order
        tabIndex={tabIndex ?? (isTabStop ? 0 : undefined)}
        {...(keepMounted ? { forceMount: true } : undefined)}
      />
    )
  }
)
TabsPanel.displayName = 'Lapidary: TabsPanel ← TabsLike'
